import type { Command, GameState, Rng } from '@sd/sim';
import type { Bot } from './match';

/**
 * A bot's attention (#148, the author's note of 27.10.2026: bots clicked every
 * option at once, as if they saw every tab and had ten hands). A person looks at
 * one part of the game at a time, learns of news with a delay, and gives a
 * handful of orders a minute; so does a bot with a mind.
 */
export type Tab = 'planet' | 'research' | 'fleet' | 'market' | 'diplomacy' | 'projects';

/** One part of a bot, and the tab it belongs to; `always` runs whatever the focus. */
export interface Module {
  readonly tab: Tab | 'always';
  readonly bot: Bot;
}

export const BOT_LEVELS = ['novice', 'normal', 'expert'] as const;
export type BotLevel = (typeof BOT_LEVELS)[number];

export interface Attention {
  /** Ticks a bot stays on one tab before it looks elsewhere. */
  readonly dwell: readonly [number, number];
  /** Ticks before it learns of news: a joint project, a proposal, a war on it. */
  readonly reaction: readonly [number, number];
  /** Ticks before it reacts to a card over the screen: a find to pick (#105). */
  readonly popup: readonly [number, number];
  /** Orders a minute, at most, and how many it may give at once. */
  readonly perMinute: number;
  readonly burst: number;
  /**
   * How much keener the modules of the open tab are than when the bot saw every
   * tab at once: a person on a tab does what it needs, then looks elsewhere.
   */
  readonly keen: number;
}

/**
 * 10 ticks a second. A «normal» bot looks at a tab for 6–15 s, learns of news in
 * 10–25 s, reads a find's cards in 2–6 s and gives at most 20 orders a minute.
 */
export const ATTENTION: Readonly<Record<BotLevel, Attention>> = {
  novice: {
    dwell: [120, 240],
    reaction: [200, 400],
    popup: [40, 100],
    perMinute: 8,
    burst: 2,
    keen: 3,
  },
  normal: {
    dwell: [60, 150],
    reaction: [100, 250],
    popup: [20, 60],
    perMinute: 20,
    burst: 4,
    keen: 5,
  },
  expert: {
    dwell: [40, 90],
    reaction: [40, 100],
    popup: [10, 30],
    perMinute: 40,
    burst: 6,
    keen: 8,
  },
};

const NOTHING = Object.freeze([]) as unknown as Command[];
const TICKS_PER_MINUTE = 600;

const between = (rng: Rng, [min, max]: readonly [number, number]) =>
  min + Math.floor(rng.next() * (max - min + 1));

/** The same dice, but a chance of `p` rolls as `p × keen`: the open tab's modules act sooner. */
function keener(rng: Rng, by: number): Rng {
  return new Proxy(rng, {
    get(target, key) {
      if (key === 'chance') return (p: number) => target.chance(Math.min(1, p * by));
      const value = Reflect.get(target, key, target) as unknown;
      return typeof value === 'function'
        ? (value as (...a: unknown[]) => unknown).bind(target)
        : value;
    },
  });
}

/**
 * A bot of `modules` that looks at one tab at a time, learns of news late and
 * keeps to its pace of orders. Stateful: one per seat and match.
 */
/** What a bot cares for most: tabs it looks at more often, as a person going for one victory. */
export type Focus = Partial<Readonly<Record<Tab, number>>>;

export function makeMind(
  modules: readonly Module[],
  level: BotLevel = 'normal',
  focus: Focus = {},
): Bot {
  const a = ATTENTION[level];
  let keenRng: { of: Rng; as: Rng } | null = null;
  const keen = (rng: Rng) => {
    if (keenRng?.of !== rng) keenRng = { of: rng, as: keener(rng, a.keen) };
    return keenRng.as;
  };
  const tabs = [...new Set(modules.flatMap((m) => (m.tab === 'always' ? [] : [m.tab])))];
  let tab: Tab | null = null;
  let until = 0;
  let tokens = a.burst;
  let last = -1;
  /** When each piece of news is noticed: its first sight plus a reaction. */
  const noticed = new Map<string, number>();

  /** The state as the bot knows it: news it has not noticed yet left out. */
  const known = (state: GameState, me: string, rng: Rng): GameState => {
    const seen = (key: string, span: readonly [number, number] = a.reaction) => {
      let at = noticed.get(key);
      if (at === undefined) {
        at = state.tick + between(rng, span);
        noticed.set(key, at);
      }
      return state.tick >= at;
    };
    const projects = state.projects.filter((p) => seen(`project:${p.id}`));
    const proposals = state.proposals.filter(
      (p) => p.to !== me || seen(`proposal:${p.from}:${p.kind}`),
    );
    const findOffers = state.findOffers.filter(
      (o) => o.playerId !== me || seen(`find:${o.id}`, a.popup),
    );
    const pendingWars = state.pendingWars.filter(
      (w) => w.target !== me || seen(`war:${w.by}:${w.startsAt}`),
    );
    const same =
      projects.length === state.projects.length &&
      proposals.length === state.proposals.length &&
      findOffers.length === state.findOffers.length &&
      pendingWars.length === state.pendingWars.length;
    return same ? state : { ...state, projects, proposals, findOffers, pendingWars };
  };

  /** Where to look next: anything pressing weighs more, the same tab twice is avoided. */
  const pick = (state: GameState, me: string, rng: Rng): Tab => {
    const player = state.players.find((p) => p.id === me);
    // A rival's countdown to victory is on everyone's screen: answering it comes first.
    const threat = state.countdowns.some((c) => c.playerId !== me) ? 3 : 1;
    const weight = (t: Tab): number => {
      if (threat > 1 && (t === 'market' || t === 'fleet')) return threat;
      switch (t) {
        case 'projects':
          return state.projects.some(
            (p) => state.tick < p.deadline && (p.byPlayer[me]?.points ?? 0) === 0,
          )
            ? 4
            : 0.5;
        case 'fleet':
          return state.wars.some((w) => w.a === me || w.b === me) ||
            state.pendingWars.some((w) => w.target === me)
            ? 3
            : 1;
        case 'research':
          return player && player.research.active.length === 0 ? 2.5 : 0.8;
        case 'diplomacy':
          return state.proposals.some((p) => p.to === me) ? 2 : 0.6;
        case 'planet':
          return 1.6;
        case 'market':
          return 1;
      }
    };
    const want = (t: Tab) => weight(t) * (focus[t] ?? 1);
    const choices = tabs.filter((t) => t !== tab || tabs.length === 1);
    const total = choices.reduce((sum, t) => sum + want(t), 0);
    let roll = rng.next() * total;
    for (const t of choices) {
      roll -= want(t);
      if (roll <= 0) return t;
    }
    return choices[choices.length - 1]!;
  };

  return (state, me, rng) => {
    if (last >= 0)
      tokens = Math.min(a.burst, tokens + ((state.tick - last) * a.perMinute) / TICKS_PER_MINUTE);
    last = state.tick;
    const view = known(state, me, rng);
    if (tab === null || state.tick >= until) {
      tab = pick(view, me, rng);
      until = state.tick + between(rng, a.dwell);
    }
    let out: Command[] | null = null;
    for (const m of modules) {
      if (m.tab !== 'always' && m.tab !== tab) continue;
      const said = m.bot(view, me, m.tab === 'always' ? rng : keen(rng));
      if (said.length === 0) continue;
      if (m.tab === 'always') {
        // Pauses and rebellions are not clicks in a tab.
        (out ??= []).push(...said);
        continue;
      }
      const room = Math.floor(tokens);
      if (room <= 0) continue;
      const given = said.slice(0, room);
      tokens -= given.length;
      (out ??= []).push(...given);
    }
    return out ?? NOTHING;
  };
}
